import { apiClient } from '@/api/client';
import type {
  ConvertLeadRequest,
  CreateLeadRequest,
  LeadResponse,
  LeadSource,
  LeadStatus,
  LeadStatusTransitionRequest,
  PaginatedLeads,
  StudentResponse,
  UpdateLeadRequest,
  UserRef,
} from '@/types/shared';

/** GET /leads/assignable-agents — active Sales Agents for the assign dropdown (Manager/Admin). */
export async function listAssignableAgents(): Promise<UserRef[]> {
  const { data } = await apiClient.get<UserRef[]>('/leads/assignable-agents');
  return data;
}

export interface ListLeadsParams {
  page?: number;
  pageSize?: number;
  /** `field:ASC` / `field:DESC` — allowed fields per sales.service.ts `applySort`: `createdAt`,
   * `fullName`, `status`, `nextFollowUpDate`. */
  sort?: string;
  search?: string;
  status?: LeadStatus;
  leadSource?: LeadSource;
  possibleDuplicate?: boolean;
  /** New-Lead queue (`assignedEmployeeId IS NULL`) — Manager/Admin. */
  unassigned?: boolean;
  /** Manager/Admin only — sales.service.ts ignores this for a Sales Agent caller (own-scope always
   * wins, never bypassable via query params — 03-rbac-and-lifecycle.md §2). */
  assignedEmployee?: string;
  /** T175/T169 (§20, openapi `BranchFilter`). Narrows WITHIN the caller's composed own+branch scope
   * only — never widens it. Omitted by the UI entirely for a single-branch caller (T159 §5.2). */
  branch?: number;
}

/** GET /leads — Roles: Admin, Sales Manager, Sales Agent. A Sales Agent sees only their own
 * assigned leads (server-side scope, never filtered client-side). */
export async function listLeads(params: ListLeadsParams): Promise<PaginatedLeads> {
  const { data } = await apiClient.get<PaginatedLeads>('/leads', { params });
  return data;
}

/** GET /leads/:id — 404 for a lead outside the caller's own-scope (no existence leak). */
export async function getLead(id: string): Promise<LeadResponse> {
  const { data } = await apiClient.get<LeadResponse>(`/leads/${encodeURIComponent(id)}`);
  return data;
}

/** POST /leads — Roles: Admin, Sales Manager, Sales Agent (staff manual create). A Sales Agent's
 * `assignedEmployee` is always forced to themself server-side regardless of what's sent. */
export async function createLead(payload: CreateLeadRequest): Promise<LeadResponse> {
  const { data } = await apiClient.post<LeadResponse>('/leads', payload);
  return data;
}

/** PATCH /leads/:id — non-status fields only (contact/interest info + an optional appended note).
 * Status changes always go through `transitionLeadStatus` instead. */
export async function updateLead(id: string, payload: UpdateLeadRequest): Promise<LeadResponse> {
  const { data } = await apiClient.patch<LeadResponse>(`/leads/${encodeURIComponent(id)}`, payload);
  return data;
}

/** DELETE /leads/:id — Roles: Admin, Sales Manager. Soft-delete. */
export async function deleteLead(id: string): Promise<void> {
  await apiClient.delete(`/leads/${encodeURIComponent(id)}`);
}

/** POST /leads/:id/restore — Admin only. */
export async function restoreLead(id: string): Promise<LeadResponse> {
  const { data } = await apiClient.post<LeadResponse>(`/leads/${encodeURIComponent(id)}/restore`);
  return data;
}

/** PATCH /leads/:id/status — enforces the lead status state machine server-side (03 §3);
 * `lib/leadStatus.ts` mirrors the same graph client-side so only a legal next status is ever
 * offered. `attendanceResult` is never sent — it's derived server-side from the target status. */
export async function transitionLeadStatus(
  id: string,
  payload: LeadStatusTransitionRequest,
): Promise<LeadResponse> {
  const { data } = await apiClient.patch<LeadResponse>(
    `/leads/${encodeURIComponent(id)}/status`,
    payload,
  );
  return data;
}

/** PATCH /leads/:id/assign — Roles: Admin, Sales Manager. Target must be an active Sales Agent
 * (`ASSIGNEE_NOT_SALES_AGENT` otherwise). */
export async function assignLead(id: string, assignedEmployee: string): Promise<LeadResponse> {
  const { data } = await apiClient.patch<LeadResponse>(`/leads/${encodeURIComponent(id)}/assign`, {
    assignedEmployee,
  });
  return data;
}

/** POST /leads/:id/convert — only a lead with `status='Attended'` may convert
 * (`LEAD_NOT_READY_FOR_CONVERSION` otherwise); a lead that already has a `studentCode`/`Registered`
 * status raises `LEAD_ALREADY_CONVERTED`. Atomic on the server: creates the Admissions + Student
 * Affairs + Accounts + Exam Bookings record set for a brand-new Student Code and flips the lead to
 * `Registered` in one transaction, or rolls back entirely. */
export async function convertLead(
  id: string,
  payload: ConvertLeadRequest,
): Promise<StudentResponse> {
  const { data } = await apiClient.post<StudentResponse>(
    `/leads/${encodeURIComponent(id)}/convert`,
    payload,
  );
  return data;
}
